import { useKeycloak } from '@react-keycloak/web';
import { useCallback, useContext } from 'react';
import { Permission, UserType } from '@cnx-saas/common';
import { UnifiedAuth, User } from '../types';
import { AuthContext } from './auth-provider';
import { login } from './login';

import { isAuthorized } from './utils';

function useAuthUser(): User | null {
  const { authenticated } = useContext(AuthContext);
  const { initialized, keycloak } = useKeycloak();
  if (!initialized || !authenticated || !keycloak.tokenParsed) return null;

  const {
    sub,
    email,
    tenant_id,
    userType = UserType.USER,
  } = keycloak.tokenParsed;

  if (!sub || !email) {
    throw new Error('Invalid Keycloak configuration');
  }

  if (!tenant_id && userType !== UserType.BACK_OFFICE) {
    throw new Error('Invalid Keycloak configuration');
  }

  if (
    tenant_id &&
    userType !== UserType.USER &&
    userType !== UserType.TEAM_ADMIN
  ) {
    throw new Error('Invalid Keycloak configuration');
  }

  return {
    userId: sub,
    email: email as string,
    userType: userType as UserType,
    ...(tenant_id ? { tenantId: tenant_id as string } : { tenantId: '' }),
  };
}

function useAuth(): UnifiedAuth {
  const { initialized, authenticated } = useContext(AuthContext);
  const { initialized: keycloakInitialized, keycloak } = useKeycloak();
  const user = useAuthUser();
  const impersonator = keycloak.tokenParsed?.impersonator as
    | { id: string }
    | undefined;

  return {
    initialized: initialized && keycloakInitialized,
    authenticated: authenticated,
    authorized: useCallback(
      (roles?: Permission[]) => isAuthorized(authenticated, keycloak, roles),
      [keycloak, authenticated],
    ),
    logout: useCallback(async () => keycloak.clearToken(), [keycloak]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    login: useCallback(login(keycloak), [keycloak]),
    token: useCallback(() => keycloak.token, [keycloak]),
    user,
    impersonator,
  };
}

export { useAuthUser, useAuth };
